import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Download, Sparkles, Megaphone, MessageCircle,
  Target, CalendarDays, Image as ImageIcon, TrendingUp, Save, RefreshCw, FileText,
  Pencil, Check, Copy, Send, Facebook, CopyPlus, Smartphone,
} from "lucide-react";
import { GFFrame } from "@/components/GFFrame";
import { aiGetCampaign, aiSavePerformance, aiCreativeUrl, aiStoryUrl, aiVariationUrl, aiSelectVariation, aiPackUrl, aiFinalizeCampaign, aiGetOwner, aiUpdateCampaign, aiDuplicateCampaign } from "@/lib/gfApi";
import { CAMPAIGN_STATUS, readAiEmail } from "@/pages/ai/aiShared";

const Section = ({ title, icon: Icon, children, testId }) => (
  <section className="mt-6 px-6" data-testid={testId}>
    <div className="mb-3 flex items-center gap-2">
      {Icon && <Icon className="h-5 w-5 text-[#D4AF37]" />}
      <h3 className="font-heading text-lg font-bold text-[#0F172A]">{title}</h3>
    </div>
    {children}
  </section>
);

const CopyBox = ({ label, text }) =>
  text ? (
    <div className="rounded-2xl border border-[#E7E2D6] bg-white p-4">
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#B8912B]">{label}</p>
      <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{text}</p>
    </div>
  ) : null;

export default function CampaignView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [perf, setPerf] = useState({ reach: "", engagement: "", leads: "", sales_rm: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [creditsLeft, setCreditsLeft] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [imgBust, setImgBust] = useState(0);
  const pollRef = useRef(null);

  const refresh = async () => {
    const data = await aiGetCampaign(id);
    setC(data);
    if (data.email) aiGetOwner(data.email).then((o) => setCreditsLeft(o.credits?.usable)).catch(() => {});
    return data;
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await refresh();
        if (data.performance) setPerf((p) => ({ ...p, ...data.performance }));
        if (data.status === "generating") startPoll();
      } catch {
        toast.error("Kempen tidak dijumpai.");
      }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [id]);

  const startPoll = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const data = await aiGetCampaign(id).catch(() => null);
      if (!data) return;
      setC(data);
      if (data.status === "completed" || data.status === "poster_failed") {
        clearInterval(pollRef.current);
        setRetrying(false);
        if (data.email) aiGetOwner(data.email).then((o) => setCreditsLeft(o.credits?.usable)).catch(() => {});
        if (data.status === "completed") toast.success("Poster siap dijana!");
      }
    }, 3000);
  };

  const retry = async () => {
    setRetrying(true);
    try {
      const res = await aiFinalizeCampaign(id);
      setC(res);
      startPoll();
    } catch (e) {
      setRetrying(false);
      toast.error(e?.response?.data?.detail || "Gagal. Sila cuba lagi.");
    }
  };

  const saveName = async () => {
    const v = nameInput.trim();
    try {
      const res = await aiUpdateCampaign(id, { name: v });
      setC(res);
      setEditingName(false);
      toast.success("Nama kempen dikemas kini.");
    } catch {
      toast.error("Gagal menyimpan nama.");
    }
  };

  const captionText = () => {
    const st = c?.strategy || {};
    return st.facebook_copy || st.instagram_copy || st.ad_primary_text || st.tiktok_copy || "";
  };

  const copyCaption = async () => {
    const txt = captionText();
    if (!txt) { toast.error("Tiada caption tersedia."); return; }
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Caption disalin! Tampal semasa berkongsi poster.");
    } catch {
      toast.error("Gagal menyalin caption.");
    }
  };

  const duplicate = async () => {    setDuplicating(true);
    try {
      const res = await aiDuplicateCampaign(id);
      toast.success("Kempen disalin — laraskan & jana poster baharu.");
      navigate(`/campaigns/new?resume=${res.id}`);
    } catch (e) {
      setDuplicating(false);
      toast.error(e?.response?.data?.detail || "Gagal menyalin kempen.");
    }
  };


  const pickVariation = async (idx) => {
    if (idx === c.selected_variation) return;
    setSelecting(true);
    try {
      const res = await aiSelectVariation(id, idx);
      setC(res);
      setImgBust(Date.now());
    } catch {
      toast.error("Gagal menukar poster.");
    } finally {
      setSelecting(false);
    }
  };

  const savePerf = async () => {
    setSaving(true);
    try {
      await aiSavePerformance(id, perf);
      toast.success("Prestasi disimpan.");
    } catch {
      toast.error("Gagal menyimpan.");
    } finally {
      setSaving(false);
    }
  };

  if (!c) {
    return (
      <GFFrame testId="campaign-view-page">
        <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>
      </GFFrame>
    );
  }

  const s = c.strategy || {};
  const wa = s.whatsapp_scripts || {};
  const tg = s.ad_targeting || {};
  const done = c.status === "completed";

  return (
    <GFFrame testId="campaign-view-page">
      <div className="min-h-screen pb-16">
        <div className="bg-[#0F172A] px-6 pb-7 pt-9 text-white">
          <button onClick={() => navigate(-1)} data-testid="campaign-back-btn" className="flex items-center gap-1 text-sm text-slate-400">
            <ArrowLeft className="h-4 w-4" /> Kembali
          </button>
          <div className="mt-3 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#D4AF37]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Brand2Market AI</span>
          </div>
          {editingName ? (
            <div className="mt-2 flex items-center gap-2" data-testid="rename-row">
              <input
                data-testid="rename-input"
                autoFocus
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveName()}
                placeholder="Nama kempen"
                className="min-w-0 flex-1 rounded-xl border border-white/25 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-400 outline-none focus:border-[#D4AF37]"
              />
              <button data-testid="rename-save-btn" onClick={saveName} className="rounded-full bg-[#D4AF37] p-2 text-[#0F172A]">
                <Check className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="mt-2 flex items-start gap-2">
              <h1 className="font-heading text-2xl font-bold text-white">{c.custom_name || c.business_name}</h1>
              <button
                data-testid="rename-btn"
                onClick={() => { setNameInput(c.custom_name || ""); setEditingName(true); }}
                className="mt-1 shrink-0 text-slate-400 transition-colors hover:text-[#D4AF37]"
                aria-label="Namakan semula kempen"
              >
                <Pencil className="h-4 w-4" />
              </button>
            </div>
          )}
          <p className="mt-1 text-sm text-slate-300">{s.campaign_objective || c.objective}</p>
          <span className={`mt-3 inline-block rounded-full px-2.5 py-1 text-[11px] font-bold ${(CAMPAIGN_STATUS[c.status] || CAMPAIGN_STATUS.draft).cls}`} data-testid="campaign-status-badge">
            {(CAMPAIGN_STATUS[c.status] || CAMPAIGN_STATUS.draft).label}
          </span>
        </div>

        {c.status === "draft" && (
          <div className="mx-6 mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="campaign-draft-note">
            Kempen ini masih draf. Sambung untuk menjana strategi.
            <button onClick={() => navigate(`/campaigns/new?resume=${id}`)} className="mt-2 block font-bold underline">Sambung Kempen</button>
          </div>
        )}

        {c.status === "generating" && (
          <div className="mx-6 mt-5 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="campaign-generating-note">
            <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
            <p className="text-sm text-amber-800">Poster sedang dijana… halaman ini akan dikemas kini secara automatik.</p>
          </div>
        )}

        {c.status === "poster_failed" && (
          <div className="mx-6 mt-5 rounded-2xl border-l-4 border-rose-500 bg-rose-50 p-4" data-testid="campaign-poster-failed">
            <p className="text-sm font-semibold text-rose-700">{c.poster_error || "Poster anda belum berjaya dijana. Kredit anda belum ditolak. Cuba semula."}</p>
            <button onClick={retry} disabled={retrying} data-testid="campaign-retry-btn"
              className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-bold text-[#0F172A] disabled:opacity-60">
              {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Cuba Semula
            </button>
          </div>
        )}

        {done && (
          <Section title="Poster Kempen" icon={ImageIcon} testId="campaign-poster">
            <img
              data-testid="campaign-creative-img"
              src={`${aiCreativeUrl(id)}?v=${imgBust}`}
              alt="Poster kempen"
              className="w-full rounded-2xl border border-[#E7E2D6]"
            />
            {c.poster_tagline && (
              <p className="mt-2 text-center text-xs italic text-slate-400" data-testid="campaign-tagline">“{c.poster_tagline}”</p>
            )}

            {c.variation_count > 1 && (
              <div className="mt-4" data-testid="variation-picker">
                <p className="text-xs font-bold uppercase tracking-wide text-[#B8912B]">Pilih rupa poster ({c.variation_count} pilihan)</p>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {Array.from({ length: c.variation_count }).map((_, i) => (
                    <button
                      key={i}
                      data-testid={`variation-thumb-${i}`}
                      onClick={() => pickVariation(i)}
                      disabled={selecting}
                      className={`relative overflow-hidden rounded-xl border-2 transition-colors ${i === c.selected_variation ? "border-[#D4AF37]" : "border-transparent opacity-80"}`}
                    >
                      <img src={aiVariationUrl(id, i)} alt={`Rupa ${i + 1}`} className="h-28 w-full object-cover" />
                      {i === c.selected_variation && (
                        <span className="absolute right-1 top-1 rounded-full bg-[#D4AF37] p-1 text-[#0F172A]"><Check className="h-3 w-3" /></span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3 grid grid-cols-3 gap-2">
              <a data-testid="campaign-download-poster" href={`${aiCreativeUrl(id)}?v=${imgBust}`} download
                className="flex items-center justify-center gap-1.5 rounded-full border-2 border-[#D4AF37] py-3 text-xs font-bold text-[#B8912B]">
                <Download className="h-4 w-4" /> Feed
              </a>
              {c.has_story && (
                <a data-testid="campaign-download-story" href={`${aiStoryUrl(id)}?v=${imgBust}`} download
                  className="flex items-center justify-center gap-1.5 rounded-full border-2 border-[#0F172A] py-3 text-xs font-bold text-[#0F172A]">
                  <Smartphone className="h-4 w-4" /> Story
                </a>
              )}
              <a data-testid="campaign-download-pack" href={aiPackUrl(id)} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-1.5 rounded-full bg-[#D4AF37] py-3 text-xs font-bold text-[#0F172A]">
                <Download className="h-4 w-4" /> Playbook
              </a>
            </div>
            <div className="mt-3 rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid="share-panel">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-[#0F172A]">Kongsi Poster</p>
                <button data-testid="copy-caption-btn" onClick={copyCaption}
                  className="flex items-center gap-1.5 rounded-full border border-[#D4AF37] px-3 py-1.5 text-xs font-bold text-[#B8912B]">
                  <Copy className="h-3.5 w-3.5" /> Salin Caption
                </button>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <a data-testid="share-whatsapp" target="_blank" rel="noreferrer" onClick={copyCaption}
                  href={`https://wa.me/?text=${encodeURIComponent(`${s.ad_headline || s.campaign_angle || c.business_name}\n${s.offer || ""}\n\nLihat poster kempen: ${aiCreativeUrl(id)}`)}`}
                  className="flex flex-col items-center gap-1 rounded-xl bg-[#25D366] py-3 text-xs font-bold text-white transition-transform active:scale-95">
                  <MessageCircle className="h-5 w-5" /> WhatsApp
                </a>
                <a data-testid="share-facebook" target="_blank" rel="noreferrer" onClick={copyCaption}
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(aiCreativeUrl(id))}&quote=${encodeURIComponent(`${s.ad_headline || c.business_name} — ${s.offer || ""}`)}`}
                  className="flex flex-col items-center gap-1 rounded-xl bg-[#1877F2] py-3 text-xs font-bold text-white transition-transform active:scale-95">
                  <Facebook className="h-5 w-5" /> Facebook
                </a>
                <a data-testid="share-telegram" target="_blank" rel="noreferrer" onClick={copyCaption}
                  href={`https://t.me/share/url?url=${encodeURIComponent(aiCreativeUrl(id))}&text=${encodeURIComponent(`${s.ad_headline || c.business_name}\n${s.offer || ""}`)}`}
                  className="flex flex-col items-center gap-1 rounded-xl bg-[#229ED9] py-3 text-xs font-bold text-white transition-transform active:scale-95">
                  <Send className="h-5 w-5" /> Telegram
                </a>
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-slate-400">Caption disalin automatik apabila anda tekan mana-mana butang kongsi — tampal semasa berkongsi.</p>
            </div>
            <button
              data-testid="duplicate-campaign-btn"
              onClick={duplicate}
              disabled={duplicating}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#0F172A] py-3 text-sm font-bold text-[#0F172A] transition-colors hover:bg-[#0F172A] hover:text-white disabled:opacity-60"
            >
              {duplicating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CopyPlus className="h-4 w-4" />} Salin Kempen (Variasi Baharu)
            </button>
          </Section>
        )}

        {done && c.diagnosis_context && (
          <section className="mt-6 px-6" data-testid="campaign-loop-closed">
            <div className="rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#1E293B] p-5 text-white">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Daripada Insight → Tindakan</p>
              <div className="mt-3 space-y-1.5 text-sm">
                <p><span className="text-slate-400">Diagnosis:</span> <b>{c.diagnosis_context.bottleneck}</b></p>
                <p><span className="text-slate-400">Tindakan diambil:</span> Kempen dicipta</p>
                <p><span className="text-slate-400">Objektif kempen:</span> {s.campaign_objective || c.objective}</p>
                <p><span className="text-slate-400">Baki kredit kempen:</span> <b>{creditsLeft ?? "—"}</b></p>
              </div>
              <p className="mt-3 text-sm text-slate-300">Anda telah mengambil satu tindakan berdasarkan diagnosis Business Growth anda.</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <button onClick={() => navigate(`/campaigns?email=${encodeURIComponent(c.email || readAiEmail())}`)} data-testid="loop-view-campaigns"
                  className="rounded-full bg-[#D4AF37] py-2.5 text-sm font-bold text-[#0F172A]">Kempen Saya</button>
                <button onClick={() => navigate(`/report/${c.assessment_id}`)} data-testid="loop-back-report"
                  className="flex items-center justify-center gap-1.5 rounded-full border border-white/25 py-2.5 text-sm font-bold text-white">
                  <FileText className="h-4 w-4" /> Laporan
                </button>
              </div>
            </div>
          </section>
        )}

        {s.campaign_objective && (
          <>
            {s.marketing_not_priority && (
              <div className="mx-6 mt-6 rounded-2xl border-l-4 border-amber-500 bg-amber-50 p-4" data-testid="campaign-mnp">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Nota Strategik</p>
                <p className="mt-1 text-sm text-slate-700">{s.marketing_not_priority_reason}</p>
              </div>
            )}

            <Section title="Diagnosis & Sasaran" icon={Target} testId="campaign-strategy">
              <div className="space-y-2 rounded-2xl border border-[#E7E2D6] bg-white p-4 text-sm text-slate-600">
                <p><b className="text-[#0F172A]">Sasaran utama:</b> {s.primary_target}</p>
                {s.secondary_target && <p><b className="text-[#0F172A]">Sasaran kedua:</b> {s.secondary_target}</p>}
                <p><b className="text-[#0F172A]">Persona:</b> {s.persona}</p>
                <p><b className="text-[#0F172A]">Titik kesakitan:</b> {s.pain_point}</p>
                <p><b className="text-[#0F172A]">Pencetus pembelian:</b> {s.buying_trigger}</p>
                <p><b className="text-[#0F172A]">Positioning:</b> {s.positioning}</p>
                <p><b className="text-[#0F172A]">Value proposition:</b> {s.value_proposition}</p>
                <p><b className="text-[#0F172A]">Angle kempen:</b> {s.campaign_angle}</p>
                <p><b className="text-[#0F172A]">Tawaran:</b> {s.offer}</p>
                <p><b className="text-[#0F172A]">CTA:</b> {s.cta}</p>
                <p><b className="text-[#0F172A]">Platform:</b> {s.recommended_platform}</p>
              </div>
            </Section>

            <Section title="Copywriting Pack" icon={Megaphone} testId="campaign-copy">
              <div className="space-y-3">
                <CopyBox label="Facebook" text={s.facebook_copy} />
                <CopyBox label="Instagram" text={s.instagram_copy} />
                <CopyBox label="TikTok" text={s.tiktok_copy} />
                <div className="rounded-2xl bg-[#0F172A] p-4 text-white">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37]">Iklan Berbayar</p>
                  <p className="mt-1.5 text-sm font-bold">{s.ad_headline}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-300">{s.ad_primary_text}</p>
                  <p className="mt-2 text-sm text-[#D4AF37]">{s.ad_cta}</p>
                </div>
              </div>
            </Section>

            {s.content_plan?.length > 0 && (
              <Section title="Pelan Kandungan 7 Hari" icon={CalendarDays} testId="campaign-content-plan">
                <div className="space-y-2">
                  {s.content_plan.map((d, i) => (
                    <div key={i} className="rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid={`content-day-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0F172A] text-[11px] font-bold text-[#D4AF37]">{d.day || i + 1}</span>
                        <span className="text-sm font-bold text-[#0F172A]">{d.format}</span>
                      </div>
                      <p className="mt-2 text-sm font-semibold text-[#B8912B]">{d.hook}</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{d.caption}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            <Section title="Skrip Jualan WhatsApp" icon={MessageCircle} testId="campaign-whatsapp">
              <div className="space-y-2">
                {[["Mesej pertama", "first"], ["Penerangan", "explain"], ["Harga", "price"], ["Follow-up", "followup"], ["Handle bantahan", "objection"], ["Fikir dulu", "thinking"], ["Closing", "closing"]].map(([lab, k]) =>
                  wa[k] ? (
                    <div key={k} className="rounded-2xl border border-[#E7E2D6] bg-white p-4">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">{lab}</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{wa[k]}</p>
                    </div>
                  ) : null
                )}
              </div>
            </Section>

            {(tg.age || tg.location) && (
              <Section title="Sasaran Iklan" icon={Target} testId="campaign-targeting">
                <div className="grid grid-cols-2 gap-2 rounded-2xl border border-[#E7E2D6] bg-white p-4 text-sm text-slate-600">
                  {tg.age && <p><b className="text-[#0F172A]">Umur:</b> {tg.age}</p>}
                  {tg.location && <p><b className="text-[#0F172A]">Lokasi:</b> {tg.location}</p>}
                  {tg.interests && <p className="col-span-2"><b className="text-[#0F172A]">Minat:</b> {tg.interests}</p>}
                  {tg.behaviour && <p className="col-span-2"><b className="text-[#0F172A]">Tingkah laku:</b> {tg.behaviour}</p>}
                  {tg.budget_rm && <p className="col-span-2"><b className="text-[#0F172A]">Bajet:</b> {tg.budget_rm}</p>}
                </div>
              </Section>
            )}
          </>
        )}

        {done && (
          <Section title="Prestasi Kempen" icon={TrendingUp} testId="campaign-performance">
            <div className="space-y-3 rounded-2xl border border-[#E7E2D6] bg-white p-4">
              {[["Jangkauan (reach)", "reach"], ["Penglibatan", "engagement"], ["Leads", "leads"], ["Jualan (RM)", "sales_rm"]].map(([lab, k]) => (
                <div key={k}>
                  <label className="text-xs font-semibold text-slate-500">{lab}</label>
                  <input
                    data-testid={`perf-${k}`}
                    className="mt-1 w-full rounded-xl border border-[#E7E2D6] px-3 py-2.5 text-sm outline-none focus:border-[#D4AF37]"
                    value={perf[k]} onChange={(e) => setPerf({ ...perf, [k]: e.target.value })}
                  />
                </div>
              ))}
              <textarea
                data-testid="perf-notes" rows={3} placeholder="Catatan..."
                className="w-full rounded-xl border border-[#E7E2D6] px-3 py-2.5 text-sm outline-none focus:border-[#D4AF37]"
                value={perf.notes} onChange={(e) => setPerf({ ...perf, notes: e.target.value })}
              />
              <button
                data-testid="save-performance-btn" onClick={savePerf} disabled={saving}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0F172A] py-3 text-sm font-bold text-white disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan Prestasi
              </button>
            </div>
          </Section>
        )}
      </div>
    </GFFrame>
  );
}
